import { Images, LayoutGrid, ShieldCheck, SlidersHorizontal } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card } from 'src/shared/components/ui/card'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const REASONS = [
  {
    icon: ShieldCheck,
    art: '/home/why-klayder-human.webp?v=white',
    title: {
      en: 'A real human on every order',
      sv: 'En riktig människa på varje order',
    },
    body: {
      en: 'Someone at Klayder checks the quote, sizes, print and delivery before you can approve it. Sara is optional. Humans are not.',
      sv: 'Någon på Klayder går igenom offerten, storlekar, tryck och leverans innan ni kan godkänna. Sara är valfri. Människor är det inte.',
    },
  },
  {
    icon: SlidersHorizontal,
    art: '/home/why-klayder-workflow.webp?v=white',
    title: {
      en: 'Work your way',
      sv: 'Jobba som ni vill',
    },
    body: {
      en: 'Brief the team in the conversation, ask Sara when you want help finding products or making mockups, or switch between both during the same request.',
      sv: 'Briefa teamet i konversationen, be Sara när ni vill ha hjälp att hitta produkter eller ta fram mockups, eller växla mellan båda i samma förfrågan.',
    },
  },
  {
    icon: Images,
    art: '/home/why-klayder-preview.webp?v=white',
    title: {
      en: 'See it before it ships',
      sv: 'Se det innan det skickas',
    },
    body: {
      en: 'Build kits, create mockups on real products, leave feedback and approve factory proofs before production starts.',
      sv: 'Bygg kit, skapa mockups på riktiga produkter, lämna feedback och godkänn fabrikskorrektur innan produktionen startar.',
    },
  },
  {
    icon: LayoutGrid,
    art: '/home/why-klayder-delivery.webp?v=white',
    title: {
      en: 'From products to delivery',
      sv: 'Från produkter till leverans',
    },
    body: {
      en: 'Explore 10,000+ products and 30,000+ variations, source anything missing, then manage production, tracking and delivery with the same team.',
      sv: 'Utforska 10 000+ produkter och 30 000+ variationer, ta fram det som saknas, och hantera produktion, spårning och leverans med samma team.',
    },
  },
] as const

export const WhyKlayderSection = () => {
  const { t } = useLocalization()

  return (
    <Column id="why-us" className="w-full scroll-mt-24 items-center gap-10 md:gap-14">
      <Column className="relative w-full items-center overflow-hidden rounded-[32px] px-6 py-24 md:rounded-[44px] md:px-16 md:py-32">
        <div
          className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(0,0,0,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.025)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(circle_at_50%_42%,black,transparent_68%)] [-webkit-mask-image:radial-gradient(circle_at_50%_42%,black,transparent_68%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden
        >
          <div className="absolute size-[420px] rounded-full border border-stone-200/70 md:size-[560px]" />
          <div className="absolute size-[580px] rounded-full border border-stone-200/50 md:size-[760px]" />
        </div>

        <Column className="relative z-10 max-w-2xl items-center gap-4 text-center">
          <h2 className="text-balance text-4xl font-normal tracking-tight text-neutral-900 md:text-5xl">
            {t(
              'Because company merch has to be right.',
              'För att företagsmerch måste bli rätt.'
            )}
          </h2>
        </Column>
      </Column>

      <Column className="w-full max-w-6xl items-center gap-4">
        {[REASONS.slice(0, 2), REASONS.slice(2, 4)].map((row, rowIndex) => (
          <Row
            key={rowIndex}
            className="w-full !flex-col items-stretch gap-4 md:!flex-row"
          >
            {row.map((reason) => {
              const Icon = reason.icon
              return (
                <Card
                  key={reason.title.en}
                  wrapperClassName="flex-1 min-w-0"
                  className="relative h-full min-h-[14rem] overflow-hidden p-5"
                >
                  <img
                    src={reason.art}
                    alt=""
                    aria-hidden
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[82%_center] opacity-80"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white from-[28%] via-white/80 to-white/15"
                    aria-hidden
                  />
                  <Column className="relative z-10 max-w-sm gap-3">
                    <Column className="size-10 items-center justify-center rounded-xl bg-amber-50">
                      <Icon
                        className="size-5 text-amber-600"
                        strokeWidth={1.75}
                      />
                    </Column>
                    <h3>{t(reason.title.en, reason.title.sv)}</h3>
                    <p className="text-muted-foreground">
                      {t(reason.body.en, reason.body.sv)}
                    </p>
                  </Column>
                </Card>
              )
            })}
          </Row>
        ))}
      </Column>
    </Column>
  )
}
